// crewbit-admin/src/components/money/PeriodSelector.tsx
//
// SPEC §Overview.7.2. Inline pill-row period toggle for the Money home.
// 5 buttons: Last 7 / Last 30 / MTD / QTD / YTD. Custom-range affordance
// is deferred (Session 119 §2.6 / §AdminPeriodCustomRange).
//
// Style mirrors FinancialsPage tab-pill at lines 152-162: active uses
// palette.g800 background + white text; inactive uses transparent
// background + palette.g500 text inside a palette.g100 wrapper. Token-only.

import type { FinancialSummaryPeriod } from '../../hooks/useFinancialSummary';
import { palette, transition } from '../../styles/tokens';

interface PeriodSelectorProps {
  value: FinancialSummaryPeriod;
  onChange: (p: FinancialSummaryPeriod) => void;
}

interface Option {
  key: FinancialSummaryPeriod;
  label: string;
}

const OPTIONS: Option[] = [
  { key: '7d', label: 'Last 7' },
  { key: '30d', label: 'Last 30' },
  { key: 'mtd', label: 'MTD' },
  { key: 'qtd', label: 'QTD' },
  { key: 'ytd', label: 'YTD' },
];

export function PeriodSelector({ value, onChange }: PeriodSelectorProps) {
  return (
    <div
      role="tablist"
      aria-label="Period"
      style={{
        display: 'flex',
        gap: 0,
        background: palette.g100,
        borderRadius: 8,
        padding: 2,
        width: 'fit-content',
      }}
    >
      {OPTIONS.map((opt) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.key)}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: 'inherit',
              background: active ? palette.g800 : 'transparent',
              color: active ? palette.white : palette.g500,
              transition: `all ${transition.fast}`,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
